// Hook for managing sidebar chat with page-specific context
// Uses stateless SSE-based chat (no WebSocket fallback)
// @ts-nocheck - Complex type inference issues with PageContext union types

import {
  completeRun,
  migrateConversation as migrateSummaryConversation,
  setHydrationReplay,
  startRun,
} from '@platform/summary-ui';
import type { MessageEntity, TimelineEntity, ToolCallEntity } from '@platform/timeline';
import {
  addEntity,
  clearConversation,
  migrateConversation as migrateTimelineConversation,
  selectConversationEntities,
  selectHasStreamingMessages,
  setActiveConversation,
  setConversationDisplayMode,
} from '@platform/timeline';
import type { PageContext } from '@shared/types/pageContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { RootState } from '../../../store/store';
import { useLazyGetChatConversationRestoreQuery } from '../api/chatRestoreApi';
import { routeSemEvent, type SemEvent } from '../handlers/semEventHandler';
import { chatMessageEntrypoint } from '../messageEntrypoint';
import { RUN_FAILURE_FALLBACK_MESSAGE } from '../runFailureMessage';
import { useStatelessChat } from './useStatelessChat';

export interface SidebarChatRestoreProjection {
  suppressLeadingEntrypoints?: string[];
  introText?: string;
}

export interface UseSidebarChatOptions {
  // Conversation configuration
  convId?: string;
  profile?: string;
  draftBundleId?: string; // Optional UUID of draft bundle to use for prompt resolution
  autoConnect?: boolean;
  displayMode?: 'chat' | 'ui';

  // Context injection
  context?: PageContext;
  onContextChange?: (ctx: PageContext) => void;

  // Event handlers
  onToolCall?: (tool: ToolCallEntity) => void | Promise<void>;
  onMessageComplete?: (msg: MessageEntity) => void;
  onError?: (error: Error) => void;
  onRestoreError?: (error: Error) => void;
  // Restore hydration waits for autoConnect=true, so callers can defer restore until
  // the intended profile/projection context is ready.
  restoreProjection?: SidebarChatRestoreProjection;

  // Lifecycle
  clearOnUnmount?: boolean;
}

export interface UseSidebarChatReturn {
  // Chat state
  entities: TimelineEntity[];
  isStreaming: boolean;
  convId: string;

  // Actions
  sendMessage: (text: string) => Promise<void>;
  startWithHiddenMessage: (text: string) => Promise<void>;
  injectSystemMessage: (text: string) => void;
  updateContext: (ctx: Partial<PageContext>) => void;
  clearChat: () => void;

  // Connection state
  isConnected: boolean;
  error: string | null;
  isHydratingReplay: boolean;
}

const applyRestoreProjection = <
  T extends {
    run_id: string;
    event_type: string;
    payload: Record<string, unknown>;
  },
>(
  events: T[],
  projection?: SidebarChatRestoreProjection
) => {
  const introText = projection?.introText?.trim() ?? '';
  const suppressedEntrypoints = new Set(
    (projection?.suppressLeadingEntrypoints ?? [])
      .map((entrypoint) => entrypoint.trim())
      .filter((entrypoint) => entrypoint.length > 0)
  );

  if (suppressedEntrypoints.size === 0) {
    return {
      events,
      introText: introText.length > 0 && events.length > 0 ? introText : null,
    };
  }

  const suppressedRunIDs = new Set<string>();

  for (const evt of events) {
    if (evt.event_type !== 'user') {
      continue;
    }

    const payload = evt.payload as { message_entrypoint?: unknown };
    const entrypoint =
      typeof payload?.message_entrypoint === 'string' ? payload.message_entrypoint.trim() : '';

    if (entrypoint.length === 0 || !suppressedEntrypoints.has(entrypoint)) {
      break;
    }

    suppressedRunIDs.add(evt.run_id);
  }

  const filteredEvents =
    suppressedRunIDs.size === 0
      ? events
      : events.filter((evt) => !suppressedRunIDs.has(evt.run_id));

  return {
    events: filteredEvents,
    introText: introText.length > 0 && suppressedRunIDs.size > 0 ? introText : null,
  };
};

export function useSidebarChat(options: UseSidebarChatOptions): UseSidebarChatReturn {
  const {
    convId: providedConvId,
    profile = 'default',
    draftBundleId,
    autoConnect = true,
    displayMode = 'chat',
    context,
    onContextChange,
    onToolCall,
    onMessageComplete,
    onError,
    onRestoreError,
    restoreProjection,
    clearOnUnmount = false,
  } = options;

  const dispatch = useDispatch();
  const store = useStore<RootState>();

  // Generate a stable fallback ID when none is provided.
  // If a caller provides convId and later changes it, we follow that change.
  const generatedConvIdRef = useRef<string>(providedConvId || `conv-${Date.now()}`);
  const convId = providedConvId || generatedConvIdRef.current;

  // Local context state
  const [pageContext, setPageContext] = useState<PageContext | undefined>(context);
  const [isHydratingReplay, setIsHydratingReplayState] = useState(false);
  const [restoreAttemptNonce, setRestoreAttemptNonce] = useState(0);

  const [fetchConversationRestore] = useLazyGetChatConversationRestoreQuery();
  const restoredConversationRef = useRef<string | null>(null);
  const restoreInFlightKeyRef = useRef<string | null>(null);
  const restoreAttemptCountRef = useRef<Map<string, number>>(new Map());
  const seenEventKeysRef = useRef<Set<string>>(new Set());

  const toErrorWithStatus = useCallback((err: unknown, fallbackMessage: string): Error => {
    if (err instanceof Error) {
      return err;
    }

    const nextError = new Error(fallbackMessage) as Error & { status?: number };
    if (typeof err === 'object' && err !== null && 'status' in err) {
      const status = (err as { status?: unknown }).status;
      if (typeof status === 'number') {
        nextError.status = status;
      }
    }
    return nextError;
  }, []);

  const shouldProcessFrame = useCallback(({ runId, seq }: { runId: string; seq?: number }) => {
    if (typeof seq !== 'number') {
      return true;
    }
    const key = `${runId}:${seq}`;
    if (seenEventKeysRef.current.has(key)) {
      return false;
    }
    seenEventKeysRef.current.add(key);
    return true;
  }, []);

  // Refs to decouple callback identities from frequently changing values
  const pageContextRef = useRef(pageContext);
  const onErrorRef = useRef(onError);
  const onRestoreErrorRef = useRef(onRestoreError);
  const onContextChangeRef = useRef(onContextChange);

  useEffect(() => {
    pageContextRef.current = pageContext;
  }, [pageContext]);

  // no-op: mutation loading replaced by central queue

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onRestoreErrorRef.current = onRestoreError;
  }, [onRestoreError]);

  useEffect(() => {
    onContextChangeRef.current = onContextChange;
  }, [onContextChange]);

  // Connect via stateless SSE (no WebSocket)
  const {
    connected: _sseConnected, // SSE connection status (per-run, not persistent)
    isRunning,
    serverConvId,
    error: chatError,
    sendMessage: statelessSendMessage,
  } = useStatelessChat({
    convId,
    profile,
    draftBundleId,
    enabled: autoConnect,
    shouldProcessFrame,
  });

  // Use server's conversation ID for Redux queries (events are routed there)
  const effectiveConvId = serverConvId || convId;

  // Migrate timeline entities when the server assigns a canonical conversation ID.
  // Without this, optimistic entities (e.g. the user message) added under the local
  // client-generated convId would be orphaned and disappear from the chat view once
  // effectiveConvId switches to the server UUID.
  const migrationKeyRef = useRef<string | null>(null);
  const migrationConvIdRef = useRef<string | null>(convId);

  useEffect(() => {
    if (migrationConvIdRef.current !== convId) {
      migrationConvIdRef.current = convId;
      migrationKeyRef.current = null;
    }

    if (serverConvId && serverConvId !== convId) {
      const migrationKey = `${convId}->${serverConvId}`;
      if (migrationKeyRef.current === migrationKey) {
        return;
      }
      migrationKeyRef.current = migrationKey;
      dispatch(migrateTimelineConversation({ fromConvId: convId, toConvId: serverConvId }));
      dispatch(migrateSummaryConversation({ fromConvId: convId, toConvId: serverConvId }));
    }
  }, [dispatch, serverConvId, convId]);

  useEffect(() => {
    if (!effectiveConvId) {
      return;
    }
    seenEventKeysRef.current.clear();
  }, [effectiveConvId]);

  useEffect(() => {
    if (!autoConnect || !providedConvId || restoredConversationRef.current === providedConvId) {
      return;
    }
    const restoreRequestKey = `${providedConvId}:${restoreAttemptNonce}`;
    if (restoreInFlightKeyRef.current === restoreRequestKey) {
      return;
    }

    const priorAttempts = restoreAttemptCountRef.current.get(providedConvId) ?? 0;
    if (priorAttempts >= 2) {
      return;
    }

    restoreInFlightKeyRef.current = restoreRequestKey;
    restoreAttemptCountRef.current.set(providedConvId, priorAttempts + 1);
    setIsHydratingReplayState(true);
    dispatch(setHydrationReplay({ convId: providedConvId, isHydratingReplay: true }));

    let cancelled = false;
    const replayStableIDByRunID = new Map<string, string>();
    void fetchConversationRestore({
      conversationId: providedConvId,
      maxEvents: 500,
      maxRuns: 20,
    })
      .unwrap()
      .then((restore) => {
        if (cancelled) {
          return;
        }

        const replayNow = Date.now();
        const projectedRestore = applyRestoreProjection(restore.events, restoreProjection);
        const projectedEvents = projectedRestore.events;

        if (projectedRestore.introText) {
          const firstVisibleEventTimestamp = projectedEvents[0]?.created_at
            ? Date.parse(projectedEvents[0].created_at)
            : Number.NaN;
          const introTimestamp = Number.isFinite(firstVisibleEventTimestamp)
            ? Math.max(0, firstVisibleEventTimestamp - 1)
            : replayNow;

          dispatch(
            addEntity({
              convId: providedConvId,
              entity: {
                id: `restore-intro:${providedConvId}`,
                kind: 'message',
                timestamp: introTimestamp,
                props: {
                  role: 'assistant',
                  content: projectedRestore.introText,
                  metadata: { type: 'system' },
                },
              } as MessageEntity,
            })
          );
        }

        for (const evt of projectedEvents) {
          if (!shouldProcessFrame({ runId: evt.run_id, seq: evt.seq })) {
            continue;
          }

          if (evt.event_type === 'user') {
            const payload = evt.payload as { text?: unknown; client_message_id?: unknown };
            const content = typeof payload?.text === 'string' ? payload.text.trim() : '';
            if (content.length === 0) {
              continue;
            }

            const restoredTimestamp = Date.parse(evt.created_at);
            const timestamp = Number.isFinite(restoredTimestamp) ? restoredTimestamp : replayNow;
            const clientMessageID =
              typeof payload?.client_message_id === 'string'
                ? payload.client_message_id.trim()
                : '';
            const userMessageID =
              clientMessageID.length > 0
                ? `restore-user:${clientMessageID}`
                : `restore-user:${evt.run_id}:${evt.seq}`;

            dispatch(
              addEntity({
                convId: providedConvId,
                entity: {
                  id: userMessageID,
                  kind: 'message',
                  timestamp,
                  props: {
                    role: 'user',
                    content,
                  },
                } as MessageEntity,
              })
            );
            continue;
          }

          if (evt.event_type === 'sem') {
            const activeRunBeforeSem =
              store.getState().summaryUi?.byConvId?.[providedConvId]?.activeRun;
            const payload = evt.payload as { sem?: boolean; event?: SemEvent };
            if (payload?.sem && payload?.event) {
              if (payload.event.type === 'run.failed') {
                const runIdFromEvent =
                  typeof (payload.event.data as { run_id?: unknown })?.run_id === 'string'
                    ? (payload.event.data as { run_id: string }).run_id
                    : payload.event.id;
                const userMessage =
                  typeof (payload.event.data as { user_message?: unknown })?.user_message ===
                  'string'
                    ? (payload.event.data as { user_message: string }).user_message
                    : RUN_FAILURE_FALLBACK_MESSAGE;

                if (runIdFromEvent) {
                  dispatch(
                    startRun({ convId: providedConvId, stableId: runIdFromEvent, now: replayNow })
                  );
                  dispatch(
                    completeRun({
                      convId: providedConvId,
                      stableId: runIdFromEvent,
                      success: false,
                      error: userMessage,
                      now: replayNow,
                    })
                  );
                }
              }

              routeSemEvent({
                dispatch,
                convId: providedConvId,
                ev: payload.event,
                now: replayNow,
              });

              const activeRunAfterSem =
                store.getState().summaryUi?.byConvId?.[providedConvId]?.activeRun;
              if (
                activeRunAfterSem?.stableId &&
                activeRunAfterSem.stableId !== activeRunBeforeSem?.stableId
              ) {
                replayStableIDByRunID.set(evt.run_id, activeRunAfterSem.stableId);
              }
            }
            continue;
          }

          if (
            evt.event_type === 'done' ||
            evt.event_type === 'failed' ||
            evt.event_type === 'cancelled'
          ) {
            const stableId = replayStableIDByRunID.get(evt.run_id);
            if (!stableId) {
              continue;
            }

            const payload = evt.payload as { error_message?: unknown };
            const errorMessage =
              evt.event_type === 'failed' && typeof payload?.error_message === 'string'
                ? payload.error_message
                : undefined;
            dispatch(
              completeRun({
                convId: providedConvId,
                stableId,
                success: evt.event_type !== 'failed',
                error: errorMessage,
                now: replayNow,
              })
            );
          }
        }

        restoredConversationRef.current = providedConvId;

        if (restore.restore_status === 'empty' && !projectedRestore.introText) {
          dispatch(
            addEntity({
              convId: providedConvId,
              entity: {
                id: `restore-status-empty:${Date.now()}`,
                kind: 'status',
                timestamp: Date.now(),
                props: {
                  text: 'No previous chat history was found for this conversation.',
                  type: 'info',
                  source: 'rehydration',
                },
              } as any,
            })
          );
        }

        if (restore.restore_status === 'partial') {
          dispatch(
            addEntity({
              convId: providedConvId,
              entity: {
                id: `restore-status-partial:${Date.now()}`,
                kind: 'status',
                timestamp: Date.now(),
                props: {
                  text: 'Partial chat history was restored.',
                  type: 'info',
                  source: 'rehydration',
                },
              } as any,
            })
          );
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        restoredConversationRef.current = null;
        const restoreError = toErrorWithStatus(err, 'Failed to restore conversation');
        onRestoreErrorRef.current?.(restoreError);
        onErrorRef.current?.(restoreError);
        const attemptsForConversation = restoreAttemptCountRef.current.get(providedConvId) ?? 0;
        if (attemptsForConversation < 2) {
          setRestoreAttemptNonce((prev) => prev + 1);
        }
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        if (restoreInFlightKeyRef.current === restoreRequestKey) {
          restoreInFlightKeyRef.current = null;
        }
        setIsHydratingReplayState(false);
        dispatch(setHydrationReplay({ convId: providedConvId, isHydratingReplay: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    fetchConversationRestore,
    autoConnect,
    providedConvId,
    restoreAttemptNonce,
    shouldProcessFrame,
    store,
    toErrorWithStatus,
    restoreProjection,
  ]);

  // Set active conversation when effectiveConvId is known
  useEffect(() => {
    dispatch(setActiveConversation(effectiveConvId));
  }, [dispatch, effectiveConvId]);

  useEffect(() => {
    dispatch(setConversationDisplayMode({ convId: effectiveConvId, displayMode }));
  }, [dispatch, effectiveConvId, displayMode]);

  // Get entities from Redux using the effective conversation ID
  const entities = useSelector(selectConversationEntities(effectiveConvId));
  const hasStreamingMessages = useSelector(selectHasStreamingMessages(effectiveConvId));
  const isRunInFlight = isRunning;

  // Track tool calls and handle them
  const handledToolCallsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!onToolCall) return;

    const toolCalls = entities.filter((e): e is ToolCallEntity => e.kind === 'tool_call');

    for (const toolCall of toolCalls) {
      if (!handledToolCallsRef.current.has(toolCall.id)) {
        handledToolCallsRef.current.add(toolCall.id);

        // Call handler asynchronously
        Promise.resolve(onToolCall(toolCall)).catch((err) => {
          console.error('Error handling tool call:', err);
          onError?.(err instanceof Error ? err : new Error(String(err)));
        });
      }
    }
  }, [entities, onToolCall, onError]);

  // Track message completions
  const handledMessagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!onMessageComplete) return;

    const messages = entities.filter(
      (e): e is MessageEntity =>
        e.kind === 'message' && e.props.role === 'assistant' && !e.props.streaming
    );

    for (const message of messages) {
      if (!handledMessagesRef.current.has(message.id)) {
        handledMessagesRef.current.add(message.id);
        onMessageComplete(message);
      }
    }
  }, [entities, onMessageComplete]);

  // Update context when options.context changes (avoid churn when equivalent)
  useEffect(() => {
    if (!context) return;
    setPageContext((prev) => {
      if (!prev) return context;
      const shallowEqual = (a: PageContext, b: PageContext) => {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.pageType !== b.pageType) return false;
        if (a.documentId !== b.documentId) return false;
        if (a.selectedText !== b.selectedText) return false;
        const metaEqual = (am: any, bm: any): boolean => {
          if (am === bm) return true;
          if (!am || !bm) return false;
          const aKeys = Object.keys(am);
          const bKeys = Object.keys(bm);
          if (aKeys.length !== bKeys.length) return false;
          for (const key of aKeys) {
            const av = am[key];
            const bv = bm[key];
            if (Array.isArray(av) && Array.isArray(bv)) {
              if (av.length !== bv.length) return false;
              for (let i = 0; i < av.length; i++) {
                if (av[i] !== bv[i]) return false;
              }
            } else if (av !== bv) {
              return false;
            }
          }
          return true;
        };
        return metaEqual(a.metadata, b.metadata);
      };
      if (shallowEqual(prev, context)) return prev;
      onContextChangeRef.current?.(context);
      return context;
    });
  }, [context]);

  // Send message via stateless SSE
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const clientMessageId = crypto.randomUUID();

      // Add user message to Redux immediately for UX
      const userMessage: MessageEntity = {
        id: `user-${Date.now()}`,
        kind: 'message',
        timestamp: Date.now(),
        props: {
          role: 'user',
          content: trimmed,
        },
      };
      dispatch(addEntity({ convId: effectiveConvId, entity: userMessage }));

      // Send via stateless SSE (creates run and streams events)
      try {
        await statelessSendMessage({
          text: trimmed,
          messageEntrypoint: chatMessageEntrypoint.typedInput,
          context: pageContextRef.current,
          clientMessageId,
        });
      } catch (err) {
        console.error('Failed to send message:', err);
        onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)));
        // Show the failure in the timeline so the optimistic message is not left looking unanswered.
        const sendFailureMessage: MessageEntity = {
          id: `send-error-${clientMessageId}`,
          kind: 'message',
          timestamp: Date.now(),
          props: {
            role: 'system',
            content: RUN_FAILURE_FALLBACK_MESSAGE,
            severity: 'error',
          },
        };
        dispatch(addEntity({ convId: effectiveConvId, entity: sendFailureMessage }));
      }
    },
    [effectiveConvId, dispatch, statelessSendMessage]
  );

  // Start hidden (no visible user message added locally)
  const startWithHiddenMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const clientMessageId = crypto.randomUUID();

      // Send via stateless SSE without adding visible user message
      try {
        await statelessSendMessage({
          text: trimmed,
          messageEntrypoint: chatMessageEntrypoint.hiddenAutostart,
          context: pageContextRef.current,
          clientMessageId,
        });
      } catch (err) {
        console.error('Failed to send hidden message:', err);
        onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)));
      }
    },
    [statelessSendMessage]
  );

  // Inject system message
  const injectSystemMessage = useCallback(
    (text: string) => {
      const systemMessage: MessageEntity = {
        id: `system-${Date.now()}`,
        kind: 'message',
        timestamp: Date.now(),
        props: {
          role: 'assistant',
          content: text,
          metadata: { type: 'system' },
        },
      };
      dispatch(addEntity({ convId: effectiveConvId, entity: systemMessage }));
    },
    [effectiveConvId, dispatch]
  );

  // Update context
  const updateContext = useCallback((ctx: Partial<PageContext>) => {
    setPageContext((prev) => {
      if (!prev) return prev;
      // Only update if there are actual changes (deep-compare metadata and arrays)
      const isMetadataEqual = (a: any, b: any): boolean => {
        if (a === b) return true;
        if (!a || !b) return false;
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const key of aKeys) {
          const av = a[key];
          const bv = b[key];
          if (Array.isArray(av) && Array.isArray(bv)) {
            if (av.length !== bv.length) return false;
            for (let i = 0; i < av.length; i++) {
              if (av[i] !== bv[i]) return false;
            }
          } else if (av !== bv) {
            return false;
          }
        }
        return true;
      };
      let hasChanges = false;
      for (const key in ctx) {
        if (key === 'timestamp') continue;
        const nextVal = ctx[key as keyof PageContext];
        const prevVal = prev[key as keyof PageContext];
        if (key === 'metadata') {
          if (!isMetadataEqual(nextVal, prevVal)) {
            hasChanges = true;
            break;
          }
        } else if (nextVal !== prevVal) {
          hasChanges = true;
          break;
        }
      }
      if (!hasChanges) return prev;
      const updated = { ...prev, ...ctx, timestamp: Date.now() };
      onContextChangeRef.current?.(updated);
      return updated;
    });
  }, []);

  // Clear conversation
  const clearChat = useCallback(() => {
    dispatch(clearConversation(effectiveConvId));
    handledToolCallsRef.current.clear();
    handledMessagesRef.current.clear();
    seenEventKeysRef.current.clear();
    const convIdToReset = providedConvId || effectiveConvId;
    restoredConversationRef.current = null;
    restoreInFlightKeyRef.current = null;
    restoreAttemptCountRef.current.delete(convIdToReset);
    setRestoreAttemptNonce((prev) => prev + 1);
  }, [effectiveConvId, dispatch, providedConvId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clearOnUnmount) {
        dispatch(clearConversation(effectiveConvId));
      }
    };
  }, [clearOnUnmount, effectiveConvId, dispatch]);

  return {
    // State
    entities,
    isStreaming: hasStreamingMessages || isRunInFlight,
    convId: effectiveConvId,

    // Actions
    sendMessage,
    startWithHiddenMessage,
    injectSystemMessage,
    updateContext,
    clearChat,

    // Connection
    // In stateless mode, we're "connected" when enabled (ready to send)
    // SSE connects per-run, so we don't wait for persistent connection
    isConnected: autoConnect,
    error: chatError,
    isHydratingReplay,
  };
}
